import type { DataResidency } from "./Companies";

/** `GET /person/{person_uuid}/region`: where the Person DB stores this person. */
export interface PersonRegionApi {
  person_uuid: string;
  region: DataResidency;
}

/**
 * ActorInfoApi - User input definition
 * @interface ActorInfoApi
 */
export interface PersonInfoApi {
  birth_date?: string | null;
  names: Names[];
  jobs: Jobs[];
  addresses: Addresses[];
  degrees: Degrees[];
  phones: Phones[];
  emails: Emails[];
  identifications: Identifications[];
  modified_by?: string;
  /**
   * Hybrid rerun only: wizard staging person from `/person/create/empty` (document uploads).
   * Must differ from the parent report's subject UUID. Set via `buildHybridRerunPersonPayload`.
   * See `docs/HYBRID_RERUN.md`.
   */
  person_uuid?: string;
}

export enum NameType {
  MAIN = "MAIN",
  ALIAS = "ALIAS",
}
export interface Names {
  first_name: string;
  middle_name?: string;
  last_name: string;
  name_type: NameType;
  enriched_by_item?: string;
}

export interface Jobs {
  external_id?: string | null;
  title: string;
  employer: string;
  country: string;
  start_date: string;
  end_date: string;
  enriched_by_item?: string;
}

/** Person-domain social profile URL. Stateless vs item rows (see useGetPersonAccounts). */
export interface Account {
  external_id: string;
  url: string;
}

export interface Addresses {
  external_id?: string | null;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  start_date?: string;
  end_date?: string;
  enriched_by_item?: string;
}

export interface Degrees {
  external_id?: string | null;
  name: string;
  educator: string;
  country: string;
  start_date: string;
  end_date: string;
  enriched_by_item?: string;
}

export interface Phones {
  raw_number: string;
  enriched_by_item?: string;
}

export interface Emails {
  address: string;
  enriched_by_item?: string;
}

export interface Identifications {
  unique_id: string;
  description: string;
  enriched_by_item?: string;
}

export interface RecurCreate {
  person_uuid: string;
  company_uuid: string;
  report_uuid?: string;
  blueprint_uuid?: string;
  behavior_kit?: string;
  /** Required when run_now is false; ignored for scheduling when run_now is true. */
  start_date?: Date;
  schedule_value: string;
  custom_id?: string;
  /** When true, first rescreen is scheduled immediately; when false, start_date is used. */
  run_now: boolean;
}

export interface RecurFilterArgs {
  company_uuid: string;
  q?: string;
  active: boolean;
  /** Limit options to subscriptions the current user requested (no view-all reports permission). */
  individual?: boolean;
}

export interface RecurFilterOption {
  recur_uuid: string;
  person_uuid: string;
  full_name: string;
  custom_id: string;
}

export interface RecurArgs {
  company_uuid: string;
  q?: string;
  recur_uuids?: string;
  active?: boolean;
  page?: number;
  size?: number;
  /** Limit rows to subscriptions the current user requested (no view-all reports permission). */
  individual?: boolean;
}

export interface RecurCheckType {
  recur_blueprint_uuid: string;
  blueprint_uuid: string;
  blueprint_name: string;
  behavior_kit_uuid?: string;
  behavior_kit_name?: string;
}

export interface RecurConfigEx {
  recur_uuid: string;
  person_uuid: string;
  company_uuid: string;
  full_name: string;
  custom_id?: string;
  check_types: RecurCheckType[];
  next_run_date?: string;
  last_run_date?: string;
  last_run_uuid?: string;
  schedule_type: string;
  schedule_value: string;
  active: boolean;
  modified_by: string; // UUID
  modified_by_name: string; // email
  modified_at: string;
}

export interface RecurInstanceEx {
  start_date: string;
  request_uuid?: string;
}

/** ProductReports.status values returned by /recur/subscription_instances */
export type ProductReportStatus = "IN_PROGRESS" | "DONE" | "CANCEL";

export interface SubscriptionRecurInstanceEx extends RecurInstanceEx {
  product_report_status?: ProductReportStatus | null;
}

export interface RecurInstancePage {
  data: RecurInstanceEx[];
  offset: number;
  page: number;
  size: number;
  total: number;
}

export interface SubscriptionRecurInstancePage {
  data: SubscriptionRecurInstanceEx[];
  offset: number;
  page: number;
  size: number;
  total: number;
}

export interface RecurCancelArgs {
  recur_uuids: string;
}

export interface RecurRestartArgs {
  recur_uuid: string;
  interval: number;
  /** Required when run_now is false. */
  start_date?: string;
  run_now: boolean;
}

export interface RecurVerifyArgs {
  recur_uuid: string;
}

export interface RecurEditArgs {
  recur_uuid: string;
  next_run_date?: string;
}

export interface RecurInstanceArgs {
  recur_uuid: string;
  page: number;
  size: number;
}

export interface ParseResumeFromS3Args {
  id: string;
  bucket: string;
  key: string;
}
